import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    ViewChildren,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
    ConfirmationDialogComponent,
    ConfirmationDialogType,
    ConfirmationDialogOption,
} from '@components/confirmation-dialog/confirmation-dialog.component';
import { CeuUnit } from 'icreate/common/models/ceu-unit';
import { Model, VesselEquipment, DeviceType, DefaultDevices } from 'icreate/common/models/device';
import { FuUnit } from 'icreate/common/models/fu-unit';
import { fromEvent, lastValueFrom, Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-nested-device-list',
    templateUrl: './nested-device-list.component.html',
    styleUrl: './nested-device-list.component.css',
    standalone: false,
})
export class NestedDeviceListComponent implements OnInit, OnDestroy {
    @Input() models: Model[];
    @Input() units: ((FuUnit | CeuUnit) & { fcuIndex: number })[];
    @Input() vesselEquipment: VesselEquipment[];
    @Input() initialVesselEquipment: VesselEquipment[];
    @Input() vesselId: string;
    @Input() deviceType: DeviceType;
    @Output() onVesselEquipmentChanged: EventEmitter<VesselEquipment[]> = new EventEmitter();
    @ViewChildren('optionsCol') optionsCol: QueryList<ElementRef>;

    openedUnitOptions;
    optionsTop: string;

    private expandedUnitIds = new Set<string | number>();

    private destroy$: Subject<void> = new Subject();

    constructor(private dialog: MatDialog) {}

    @HostListener('document:click', ['$event'])
    clickOut(event: MouseEvent) {
        const isInsideAny = this.optionsCol.toArray().some(el => el.nativeElement.contains(event.target));

        if (!isInsideAny) {
            this.openedUnitOptions = null;
        }
    }

    ngOnInit(): void {
        this.units = this.units ?? [];
        this.units.sort((a, b) => {
            if (a.fcuIndex !== b.fcuIndex) {
                return a.fcuIndex - b.fcuIndex;
            }
            return a.index - b.index;
        });
        const scrollContainer = document.querySelector('.system-setting-scroll-parent');
        if (scrollContainer) {
            fromEvent(scrollContainer, 'scroll')
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    this.openedUnitOptions = null;
                });
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    handleModelSelected(selectedModel: Model, unit: FuUnit | CeuUnit) {
        if (this.vesselEquipment && this.vesselEquipment.length > 0) {
            const unitIndex = this.vesselEquipment.findIndex(equipment => equipment.designCode == unit.id);
            if (unitIndex >= 0) {
                this.vesselEquipment[unitIndex].equipmentModelId = selectedModel.id;
                return;
            }
        } else this.vesselEquipment = [];

        if (this.initialVesselEquipment && this.initialVesselEquipment.length > 0) {
            const initialEquipment = this.initialVesselEquipment.find(equipment => equipment.designCode == unit.id);
            if (initialEquipment) {
                this.vesselEquipment.push({
                    ...initialEquipment,
                    equipmentModelId: selectedModel.id,
                });
                this.onVesselEquipmentChanged.emit(this.vesselEquipment);
                return;
            }
        }

        this.vesselEquipment.push({
            name: this.deviceType,
            equipmentModelId: selectedModel.id,
            vesselId: this.vesselId,
            designCode: unit.id,
        });
        this.onVesselEquipmentChanged.emit(this.vesselEquipment);
    }

    getUnitModel(unit: FuUnit | CeuUnit) {
        if (!this.vesselEquipment || this.vesselEquipment.length == 0) return null;
        if (!this.models && this.models.length == 0) return null;
        const selectedEquipment = this.vesselEquipment.find(
            equipment => equipment.name == this.deviceType && equipment.designCode == unit.id
        );

        if (!selectedEquipment) return null;
        const selectedModel = this.models.find(model => model.id == selectedEquipment.equipmentModelId);
        if (!selectedModel) return null;
        return selectedModel;
    }

    getModelItemCode(unit: FuUnit | CeuUnit): string {
        if (!this.vesselEquipment || this.vesselEquipment.length == 0) return null;
        if (!this.models && this.models.length == 0) return null;
        const selectedEquipment = this.vesselEquipment.find(
            equipment => equipment.name == this.deviceType && equipment.designCode == unit.id
        );

        if (!selectedEquipment) return null;
        const selectedModel = this.models.find(model => model.id == selectedEquipment.equipmentModelId);
        if (!selectedModel) return null;
        return selectedModel.code;
    }

    getModelDescription(unit: FuUnit | CeuUnit): string {
        if (!this.vesselEquipment || this.vesselEquipment.length == 0) return null;
        if (!this.models && this.models.length == 0) return null;
        const selectedEquipment = this.vesselEquipment.find(
            equipment => equipment.name == this.deviceType && equipment.designCode == unit.id
        );

        if (!selectedEquipment) return null;
        const selectedModel = this.models.find(model => model.id == selectedEquipment.equipmentModelId);
        if (!selectedModel) return null;
        return selectedModel.description;
    }

    isUnitExpanded(unit: FuUnit | CeuUnit): boolean {
        return this.expandedUnitIds.has(unit.id);
    }

    setUnitExpanded(unit: FuUnit | CeuUnit, expanded: boolean): void {
        if (expanded) {
            this.expandedUnitIds.add(unit.id);
        } else {
            this.expandedUnitIds.delete(unit.id);
        }
    }

    openUnitOptions(unit: FuUnit | CeuUnit, index) {
        if (this.openedUnitOptions == unit) {
            this.openedUnitOptions = null;
            return;
        }
        this.updatePosition(index);
        this.openedUnitOptions = unit;
    }

    async deleteUnit(unit: FuUnit | CeuUnit) {
        const unitIndex = this.vesselEquipment.findIndex(equipment => equipment.designCode == unit.id);

        if (unitIndex >= 0) {
            const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
                width: '415px',
                data: {
                    type: ConfirmationDialogType.SIMPLE,
                    title: 'device-list.delete.title',
                    title2: 'device-list.delete.title2',
                    discardButton: 'dlg.cancel',
                    saveButton: 'dlg.confirm',
                    width: '452px',
                },
                disableClose: true,
            });
            const confirmationOption = await lastValueFrom(dialogRef.afterClosed());

            if (confirmationOption == ConfirmationDialogOption.SAVE) {
                if (this.vesselEquipment && this.vesselEquipment.length > 0) {
                    this.vesselEquipment.splice(unitIndex, 1);
                }
                this.expandedUnitIds.delete(unit.id);
                this.onVesselEquipmentChanged.emit(this.vesselEquipment);
            }
        }
    }

    private updatePosition(index) {
        if (!this.optionsCol) return;
        const optionsColArray = this.optionsCol.toArray();
        const selectedUnit = optionsColArray ? optionsColArray[index] : null;
        if (!selectedUnit) return;
        const unitPosition = selectedUnit.nativeElement.getBoundingClientRect();
        const scrollTop = window.scrollY;

        this.optionsTop = unitPosition.bottom + scrollTop + 'px';
    }
}
